import React from "react";
import { Link } from "react-router-dom";

import { Stack, Text } from "@/components/ui";
import { useTvSource } from "@/hooks";
import { config } from "@/utils/config";
import { openCookiePreferences } from "@/utils/osano";

import AppButtons from "../app-buttons";

interface FooterLink {
  title: string;
  /** Navigation target. Omit when the item is an action (provide `onClick`). */
  url?: string;
  /** Renders the item as a button instead of a link. Takes precedence. */
  onClick?: () => void;
}

const links: FooterLink[] = [
  ...config.navlinks.homeFooter,
  { title: "Cookie Preferences", onClick: openCookiePreferences },
];

const linkClassName = "hover:underline decoration-muted-foreground";

const FooterLinkItem: React.FC<{ link: FooterLink }> = ({ link }) => {
  const label = (
    <Text tag="span" theme="muted" size="xs" weight="medium">
      {link.title}
    </Text>
  );

  if (link.onClick) {
    return (
      <button type="button" onClick={link.onClick} className={linkClassName}>
        {label}
      </button>
    );
  }

  const isExternal = link.url!.startsWith("http");
  const Comp = (isExternal ? "a" : Link) as "a";
  const compProps = isExternal
    ? { href: link.url, target: "_blank" }
    : { to: link.url };

  return (
    <Comp {...compProps} className={linkClassName}>
      {label}
    </Comp>
  );
};

/** Full-width footer for the landing page: app-store badges above a centered
 *  row of legal links ending with the copyright notice. */
const LandingFooter: React.FC = () => {
  const isTvSource = useTvSource();

  return (
    <Stack space={6} alignItems="center" className="w-full px-6 py-8">
      {!isTvSource && <AppButtons />}

      <div className="flex flex-row flex-wrap items-center justify-center gap-x-4 gap-y-1">
        {links.map((link, idx) => (
          <React.Fragment key={idx}>
            {idx > 0 && (
              <span className="text-gray-300 dark:text-gray-700">|</span>
            )}
            <FooterLinkItem link={link} />
          </React.Fragment>
        ))}

        <span className="text-gray-300 dark:text-gray-700">|</span>
        <Text tag="span" theme="muted" size="xs" weight="medium">
          ©{new Date().getFullYear()} Truth Social
        </Text>
      </div>
    </Stack>
  );
};

export default LandingFooter;
